import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, Mail, Lock, LogIn } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import Logo from '../common/Logo'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { TurnstileCaptcha } from '../security/TurnstileCaptcha'
import toast from 'react-hot-toast'
import { useBehavioralAnalysis } from '../../hooks/useBehavioralAnalysis'
import { generateDeviceFingerprint } from '../../services/enhancedFingerprintService'
import { supabase } from '../../lib/supabase'
import { safeLocalStorage } from '../../utils/safeStorage'

const loginSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres')
})

type LoginFormData = z.infer<typeof loginSchema>

interface LoginFormProps {
  onToggleMode: () => void
  onForgotPassword: () => void
  /**
   * Hospedado dentro de outra superfície (ex.: ConversionModal): omite o bloco
   * Logo + título e NÃO navega para /app ao autenticar — o host decide o que
   * fazer com a sessão nova.
   */
  embedded?: boolean
  /**
   * A pessoa iniciou a autenticação (submit ou Google). O ConversionModal usa
   * para guardar a intenção de download antes de o OAuth/confirmação de e-mail
   * tirar o usuário da página.
   */
  onAuthStart?: () => void
}

const LoginForm: React.FC<LoginFormProps> = ({ onToggleMode, onForgotPassword, embedded = false, onAuthStart }) => {
  const { signIn, signInWithGoogle, user, loading: authLoading } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  // 🛡️ SECURITY: CAPTCHA state (protection against brute-force)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [captchaError, setCaptchaError] = useState(false)

  // 🤖 SECURITY: Behavioral Analysis (SEMANA 2)
  const { metrics, analyze, trackFieldInteraction } = useBehavioralAnalysis()

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema)
  })

  // 🔒 SECURITY: Record behavioral analysis and fingerprint after successful login
  useEffect(() => {
    const recordSecurityData = async () => {
      if (user && !authLoading) {
        const pendingData = safeLocalStorage.getItem('pending_login_security_data')

        if (pendingData) {
          try {
            const { behavioral, fingerprint, timestamp } = JSON.parse(pendingData)

            // Only process if data is recent (within last 2 minutes)
            if (Date.now() - timestamp < 2 * 60 * 1000) {
              // Record behavioral analysis
              const { error: behavioralError } = await supabase.rpc('record_behavioral_analysis', {
                p_user_id: user.id,
                p_session_id: `login_${timestamp}`,
                p_metrics: behavioral.metrics,
                p_confidence_score: behavioral.confidenceScore,
                p_suspicious_patterns: behavioral.suspiciousPatterns,
                p_recommendation: behavioral.recommendation,
                p_is_likely_bot: behavioral.isLikelyBot,
                p_action: 'login'
              })

              // Error handled silently for behavioral analysis recording

              // Record/update device fingerprint
              const { error: fpError } = await supabase.rpc('record_device_fingerprint', {
                p_user_id: user.id,
                p_fingerprint: fingerprint.fingerprint,
                p_components: fingerprint.components,
                p_confidence_score: fingerprint.confidence
              })

              // Error handled silently for device fingerprint recording
            }

            // Clear pending data
            safeLocalStorage.removeItem('pending_login_security_data')
          } catch (error) {
            safeLocalStorage.removeItem('pending_login_security_data')
          }
        }
      }
    }

    recordSecurityData()
  }, [user, authLoading])

  useEffect(() => {
    if (embedded) return
    if (user && !authLoading) {
      navigate('/app')
    }
  }, [user, authLoading, navigate, embedded])

  const handleGoogleSignIn = async () => {
    onAuthStart?.()
    try {
      setIsLoading(true)
      await signInWithGoogle()
      // The page will redirect to Google OAuth, no need for further action
    } catch (error) {
      // Error is already handled by signInWithGoogle
    } finally {
      setIsLoading(false)
    }
  }

  const onSubmit = async (data: LoginFormData) => {
    onAuthStart?.()
    // 🛡️ SECURITY CHECK 1: CAPTCHA validation (brute-force protection)
    if (!captchaToken) {
      toast.error('Por favor, complete a verificação de segurança')
      setCaptchaError(true)
      return
    }

    setIsLoading(true)
    try {
      // 🤖 SECURITY CHECK 2: Behavioral Analysis (SEMANA 2)
      const behavioralResult = analyze()

      // Block if detected as bot
      if (behavioralResult.recommendation === 'block') {
        toast.error('Login suspeito detectado. Por favor, tente novamente mais tarde.')

        // Log blocked attempt
        await supabase.from('security_logs').insert({
          event_type: 'login_blocked_behavioral',
          email: data.email,
          metadata: {
            confidence_score: behavioralResult.confidenceScore,
            suspicious_patterns: behavioralResult.suspiciousPatterns,
            recommendation: behavioralResult.recommendation,
            metrics: behavioralResult.metrics
          }
        })

        return
      }

      // 🔍 SECURITY CHECK 3: Enhanced Device Fingerprint (SEMANA 2)
      const deviceFp = await generateDeviceFingerprint()

      // Proceed with login
      await signIn(data.email, data.password, captchaToken)

      // Store security data for post-login recording
      safeLocalStorage.setItem('pending_login_security_data', JSON.stringify({
        behavioral: behavioralResult,
        fingerprint: deviceFp,
        timestamp: Date.now()
      }))

    } catch (error) {
      // Error is handled by the auth context
      // Reset CAPTCHA on error to prevent reuse
      setCaptchaToken(null)
      setCaptchaError(false)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full max-w-md mx-auto"
    >
      {!embedded && (
        <div className="text-center mb-8">
          <Logo variant="lockup-vertical" size={64} className="mb-4" />
          <h1 className="text-2xl font-bold text-ink-900 dark:text-cream-50 mb-2">Bem-vindo de volta!</h1>
          <p className="text-ink-500 dark:text-ink-300">Entre na sua conta para continuar</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-ink-700 dark:text-cream-300 mb-2">
            Email
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-400 w-5 h-5" />
            <input
              {...register('email')}
              type="email"
              id="email"
              onFocus={() => trackFieldInteraction('email')}
              className={`w-full pl-10 pr-4 py-3 bg-surface dark:bg-ink-700 border rounded-caderno-sm focus:ring-2 focus:ring-terra-500 focus:border-transparent transition-colors text-ink-900 dark:text-cream-50 ${
                errors.email ? 'border-blood-500 dark:border-blood-500' : 'border-cream-400 dark:border-ink-500'
              }`}
              placeholder="seu@email.com"
            />
          </div>
          {errors.email && (
            <p className="mt-1 text-sm text-blood-500 dark:text-blood-500">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-ink-700 dark:text-cream-300 mb-2">
            Senha
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-400 w-5 h-5" />
            <input
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              id="password"
              onFocus={() => trackFieldInteraction('password')}
              className={`w-full pl-10 pr-12 py-3 bg-surface dark:bg-ink-700 border rounded-caderno-sm focus:ring-2 focus:ring-terra-500 focus:border-transparent transition-colors text-ink-900 dark:text-cream-50 ${
                errors.password ? 'border-blood-500 dark:border-blood-500' : 'border-cream-400 dark:border-ink-500'
              }`}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-ink-400 hover:text-ink-700 dark:hover:text-cream-300"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-sm text-blood-500 dark:text-blood-500">{errors.password.message}</p>
          )}
        </div>

        {/* 🛡️ CAPTCHA: Cloudflare Turnstile (brute-force protection) */}
        <div className={captchaError ? 'border-2 border-blood-500 rounded-caderno-sm p-3 bg-blood-100 dark:bg-blood-500/20 dark:bg-blood-500/10' : ''}>
          <TurnstileCaptcha
            onVerify={(token) => {
              setCaptchaToken(token)
              setCaptchaError(false)
            }}
            onError={() => {
              // Não bloquear login se CAPTCHA falhar (fallback ativo)
              setCaptchaError(false)
            }}
            onExpire={() => {
              setCaptchaToken(null)
              toast.error('Verificação expirou. Recarregue a página.')
            }}
            size="invisible"
            allowFallback={true} // Permite login mesmo com erro de CAPTCHA
          />
          {captchaError && (
            <p className="mt-2 text-sm text-blood-500 dark:text-blood-500">
              Complete a verificação de segurança
            </p>
          )}
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center">
            <input
              type="checkbox"
              className="w-4 h-4 text-terra-500 border-cream-400 dark:border-ink-500 rounded-caderno-xs focus:ring-terra-500 bg-surface dark:bg-ink-700"
            />
            <span className="ml-2 text-sm text-ink-500 dark:text-ink-300">Lembrar de mim</span>
          </label>
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-sm text-terra-500 dark:text-terra-300 hover:text-terra-700 dark:hover:text-terra-300 font-medium"
          >
            Esqueceu a senha?
          </button>
        </div>

        <button
          type="submit"
          disabled={isLoading || authLoading || !captchaToken}
          className="w-full bg-terra-500 hover:bg-terra-700 disabled:bg-terra-300 disabled:cursor-not-allowed text-white py-3 px-4 rounded-caderno-sm font-medium transition-colors flex items-center justify-center space-x-2"
        >
          {isLoading || authLoading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <LogIn className="w-5 h-5" />
              <span>Entrar</span>
            </>
          )}
        </button>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-cream-400 dark:border-ink-500"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-surface dark:bg-ink-700 text-ink-400 dark:text-ink-300">
              Ou continue com
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={isLoading || authLoading}
          className="w-full bg-surface dark:bg-ink-700 border border-cream-400 dark:border-ink-500 hover:bg-cream-50 dark:hover:bg-ink-700 disabled:opacity-50 disabled:cursor-not-allowed text-ink-900 dark:text-cream-50 py-3 px-4 rounded-caderno-sm font-medium transition-colors flex items-center justify-center space-x-2"
        >
          {isLoading || authLoading ? (
            <div className="w-5 h-5 border-2 border-ink-900 dark:border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span>Entrar com Google</span>
            </>
          )}
        </button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-ink-500 dark:text-ink-300">
          Não tem uma conta?{' '}
          <button
            onClick={onToggleMode}
            className="text-terra-500 dark:text-terra-300 hover:text-terra-700 dark:hover:text-terra-300 font-medium"
          >
            Criar conta
          </button>
        </p>
      </div>
    </motion.div>
  )
}

export default LoginForm